/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import { Maybe, Nothing } from '@codegrade/maybe';
import { AssertionError } from '@/utils';
import { CheckPointsAutoTestStep } from './check_points';
import { CodeQualityAutoTestStep } from './code_quality';
import { CustomOutputAutoTestStep } from './custom_output';
import { IOAutoTestStep } from './io';
import { RedactedAutotTestStep } from './redacted';
import { RunProgramAutoTestStep } from './run_program';
import { UnitTestAutoTestStep } from './unit_test';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export { BaseAutoTestStep } from './base';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export { UnitTestAutoTestStep } from './unit_test';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const ALL_AUTO_TEST_STEPS = Object.freeze([
    IOAutoTestStep,
    RunProgramAutoTestStep,
    CustomOutputAutoTestStep,
    CheckPointsAutoTestStep,
    UnitTestAutoTestStep,
    CodeQualityAutoTestStep,
    RedactedAutotTestStep,
]);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const STEP_TYPES = Object.freeze([
    {
        name: 'io_test',
        title: 'IO Test',
        color: 'rgb(231, 238, 233)',
        help: 'Give input to a program and match it to a given output.',
    },
    {
        name: 'run_program',
        title: 'Run Program',
        color: 'rgb(230, 220, 205)',
        help: 'Execute a program or bash command.',
    },
    {
        name: 'custom_output',
        title: 'Capture Points',
        color: 'rgb(223, 211, 170)',
        help: 'Execute a custom test program that outputs a value between 0 and 1.',
    },
    {
        name: 'check_points',
        title: 'Checkpoint',
        color: 'rgb(214, 206, 91)',
        help: 'Stop testing this category if the amount of points is below a certain threshold.',
        meta: true,
    },
    {
        name: 'junit_test',
        title: 'Unit Test',
        color: 'rgb(247, 136, 123)',
        help: 'Run a JUnit-compatible testing framework and display the results in a nice overview.',
        meta: false,
    },
    {
        name: 'code_quality',
        title: 'Code Quality',
        color: 'rgb(224, 248, 204)',
        help: 'Run a linter or other code analysis tool to produce feedback on the submission automatically.',
        meta: false,
    },
]).map(el => Object.freeze(el));
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function createEmptyAutoTestStep(type, suite) {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    const getData = (stepType, data, override) => ({
        id: Maybe.empty(),
        description: Maybe.empty(),
        name: '',
        type: stepType,
        data,
        weight: 1,
        hidden: false,
        suite,
        trackingId: Nothing,
        redacted: false,
        ...override,
        toParse: true,
    });
    switch (type) {
        case 'io_test':
            return new IOAutoTestStep(getData(type, {
                program: '',
                inputs: [
                    {
                        name: '',
                        args: '',
                        stdin: '',
                        output: '',
                        options: ['case', 'substring', 'trailing_whitespace'],
                        weight: 1,
                    },
                ],
            }));
        case 'custom_output':
            return new CustomOutputAutoTestStep(getData(type, { regex: '\\f', program: '' }));
        case 'check_points':
            return new CheckPointsAutoTestStep(getData(type, { minPoints: 0 }, { weight: 0 }));
        case 'run_program':
            return new RunProgramAutoTestStep(getData(type, { program: '' }));
        case 'junit_test':
            return new UnitTestAutoTestStep(getData(type, {
                wrapper: undefined,
                program: '',
                metadata: undefined,
            }));
        case 'code_quality':
            return new CodeQualityAutoTestStep(getData(type, {
                wrapper: undefined,
                program: '',
                config: '',
                args: '',
                ignoreFilesNotFound: true,
                penalties: {
                    fatal: 100,
                    error: 20,
                    warning: 10,
                    info: 5,
                },
            }));
        default:
            return AssertionError.assertNever(type, 'Unknown test type!');
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function createAutoTestStepFromServerData(data, suite) {
    const newId = Maybe.of(data.id);
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    const makeData = (typ, innerData) => ({
        id: newId,
        description: data.description,
        name: data.name,
        type: typ,
        weight: data.weight,
        hidden: data.hidden,
        data: innerData,
        toParse: true,
        suite,
        trackingId: Nothing,
        redacted: data.redacted,
    });
    if (data.redacted) {
        return new RedactedAutotTestStep(makeData(data.type, data.data));
    }
    switch (data.type) {
        case 'io_test':
            return new IOAutoTestStep(makeData(data.type, data.data));
        case 'run_program':
            return new RunProgramAutoTestStep(makeData(data.type, data.data));
        case 'custom_output':
            return new CustomOutputAutoTestStep(makeData(data.type, data.data));
        case 'check_points':
            return new CheckPointsAutoTestStep(makeData(data.type, data.data));
        case 'junit_test':
            return new UnitTestAutoTestStep(makeData(data.type, data.data));
        case 'code_quality':
            return new CodeQualityAutoTestStep(makeData(data.type, data.data));
        default:
            return AssertionError.assertNever(data);
    }
}
